import { getBackendUrl } from '../lib/backendUrl'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  isStreaming?: boolean
  attachments?: ChatAttachment[]
  /** User feedback on assistant messages */
  vote?: 'up' | 'down'
  /** Generated image blob URL for preview */
  imageUrl?: string
  /** Generated image ID for download */
  imageId?: string
  /** True while image is being generated */
  isGeneratingImage?: boolean
}

export interface ChatAttachment {
  id: string
  name: string
  type: string
  size: number
  url: string
  storageId?: string
  storageName?: string
  uploadStatus?: 'uploading' | 'uploaded' | 'failed'
  uploadProgress?: number
  /** base64-encoded file data (for images) or text content (for text files) */
  data?: string
  /** 'base64' for images, 'text' for text files */
  encoding?: 'base64' | 'text'
}

export interface ChatSession {
  id: string
  name: string
  createdAt: number
  lastUsedAt: number
  classId?: string | null
  classTitle?: string | null
  messages: ChatMessage[]
  preview?: string
  summary?: string
  /** Whether older messages can be fetched via REST API */
  hasMore?: boolean
}

function messageContentKey(message: ChatMessage): string {
  return `${message.role}:${message.content}`
}

function compareMessageOrder(left: ChatMessage, right: ChatMessage): number {
  if (left.timestamp !== right.timestamp) {
    return left.timestamp - right.timestamp
  }
  return left.id.localeCompare(right.id)
}

function isAssistantStreamingPlaceholder(message: ChatMessage | undefined): boolean {
  return Boolean(
    message
    && message.role === 'assistant'
    && message.isStreaming
    && !message.content
    && !message.imageUrl
    && !message.imageId
    && !message.isGeneratingImage,
  )
}

export function mergeHistoryMessages(existingMessages: ChatMessage[], historyMessages: ChatMessage[]): ChatMessage[] {
  if (existingMessages.length === 0) return historyMessages
  if (historyMessages.length === 0) return existingMessages

  const historyIds = new Set(historyMessages.map((message) => message.id))
  const historyContentKeys = new Set(historyMessages.map(messageContentKey))
  const lastExistingMessage = existingMessages[existingMessages.length - 1]
  const lastHistoryMessage = historyMessages[historyMessages.length - 1]
  const shouldDropStreamingPlaceholder =
    isAssistantStreamingPlaceholder(lastExistingMessage)
    && lastHistoryMessage?.role === 'assistant'

  const localOnlyMessages = existingMessages.filter((message, index) => {
    if (shouldDropStreamingPlaceholder && index === existingMessages.length - 1) {
      return false
    }

    if (historyIds.has(message.id)) return false
    if (historyContentKeys.has(messageContentKey(message))) return false
    return true
  })

  if (localOnlyMessages.length === 0) return historyMessages

  const firstHistoryMessage = historyMessages[0]
  const olderLocalMessages = localOnlyMessages.filter(
    (message) => compareMessageOrder(message, firstHistoryMessage) < 0,
  )
  const newerLocalMessages = localOnlyMessages.filter(
    (message) => compareMessageOrder(message, firstHistoryMessage) >= 0,
  )

  if (firstHistoryMessage?.role === 'assistant') {
    const optimisticUserMessages = newerLocalMessages.filter((message) => message.role === 'user')
    const remainingNewerMessages = newerLocalMessages.filter((message) => message.role !== 'user')

    if (optimisticUserMessages.length > 0) {
      return [...olderLocalMessages, ...optimisticUserMessages, ...historyMessages, ...remainingNewerMessages]
    }
  }

  return [...olderLocalMessages, ...historyMessages, ...newerLocalMessages]
}

export function isSessionStreaming(session: ChatSession | null): boolean {
  if (!session || session.messages.length === 0) return false
  const lastMessage = session.messages[session.messages.length - 1]
  return Boolean(lastMessage?.isStreaming || lastMessage?.isGeneratingImage)
}

/** Backend API types — returned by REST and WebSocket */
export interface BackendChat {
  id: string
  title: string
  userId: string
  classId?: string | null
  classTitle?: string | null
  messageCount: number
  summary?: string
  createdAt: string
  updatedAt: string
}

export interface BackendMessagePart {
  type: string
  content?: string
  /** Image data for IMAGE-type parts */
  image?: { blobUrl?: string; id?: string }
  imageId?: string
  blobUrl?: string
  id?: string
  order?: number
}

export interface BackendMessageAttachment {
  storageId: string
  filename: string
  mimeType: string
  storageName?: string
}

export interface BackendMessage {
  id: string
  chatId: string
  role: 'user' | 'assistant'
  content?: string
  parts?: BackendMessagePart[]
  metadata?: {
    attachments?: BackendMessageAttachment[]
    [key: string]: unknown
  } | null
  attachments?: BackendMessageAttachment[]
  createdAt?: string
  timestamp?: string
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function buildStorageFileUrl(storageNameOrId: string): string {
  return `${getBackendUrl()}/api/storage/v1/files/blob/${storageNameOrId}`
}

export function backendChatToSession(chat: BackendChat): ChatSession {
  return {
    id: chat.id,
    name: chat.title,
    createdAt: new Date(chat.createdAt).getTime(),
    lastUsedAt: new Date(chat.updatedAt).getTime(),
    classId: chat.classId ?? null,
    classTitle: chat.classTitle ?? null,
    messages: [],
    summary: chat.summary,
    preview: chat.summary,
  }
}

export function backendMessageToChat(msg: BackendMessage): ChatMessage {
  // content may be a direct field or nested in a TEXT-type part
  const textPart = msg.parts?.find((p) => p.type !== 'IMAGE')
  const content = msg.content ?? textPart?.content ?? ''
  // timestamp may be a direct field or createdAt
  const ts = msg.timestamp ?? msg.createdAt

  const imageAttachments = (msg.parts ?? [])
    .filter((part) => part.type === 'IMAGE')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((part, index) => {
      const rawBlobUrl = part.image?.blobUrl ?? part.blobUrl ?? ''
      const imageUrl =
        rawBlobUrl && rawBlobUrl.startsWith('/')
          ? `${getBackendUrl()}${rawBlobUrl}`
          : rawBlobUrl

      return {
        id: part.image?.id ?? part.imageId ?? part.id ?? `${msg.id}-image-${index}`,
        name: `image-${index + 1}.png`,
        type: 'image/png',
        size: 0,
        url: imageUrl,
        uploadStatus: 'uploaded' as const,
        uploadProgress: 100,
        encoding: 'base64' as const,
      }
    })
    .filter((attachment) => Boolean(attachment.url))

  const rawAttachments = msg.attachments ?? msg.metadata?.attachments ?? []

  const attachments = [
    ...imageAttachments,
    ...rawAttachments.map((attachment) => ({
      id: attachment.storageId,
      name: attachment.filename,
      type: attachment.mimeType,
      size: 0,
      url: buildStorageFileUrl(attachment.storageName || attachment.storageId),
      storageId: attachment.storageId,
      storageName: attachment.storageName,
      uploadStatus: 'uploaded' as const,
      uploadProgress: 100,
      encoding: attachment.mimeType.startsWith('image/') ? 'base64' as const : 'text' as const,
    })),
  ]

  return {
    id: msg.id,
    role: msg.role,
    content,
    timestamp: ts ? new Date(ts).getTime() : Date.now(),
    attachments,
  }
}
